import * as React from 'react';
import type { strokeDashTypes } from '@react-financial-charts/core';
import { isDefined, noop } from '@react-financial-charts/core';
import { getXValue } from '@react-financial-charts/core/lib/utils/ChartDataUtil';
import { saveNodeType } from 'react-financial-charts';
import { HoverTextNearMouse } from '@react-financial-charts/interactive/lib/components/index';
import { InteractiveStraightLine, ClickableArrowHead, ClickableCircle, InteractiveText } from './index';
import { getCombinedTrendLabel, getLabelOffset } from '@/utils/combinedText';
import { getNewXY } from '@/utils/utils';
import type { HoverTextConfig, MoreProps, TransientBroadcastDragOverride } from '@/types/commontypes';
import type { DrawingSharedState } from '../../../../types/commontypes';
import { fixedAppearance } from '@/utils/defaultShapePropertyValues';
// import { calculateAngle } from "@/utils/duration";

/** EachTrendLine.onDrag 콜백의 세 번째 인자. 드래그 후 계산된 좌표 값 */
export interface TrendLineDragData {
  x1Value: number;
  y1Value: number;
  x2Value: number;
  y2Value: number;
}

export interface EachTrendLineProps {
  readonly x1Value: number;
  readonly x2Value: number;
  readonly y1Value: number;
  readonly y2Value: number;
  readonly index?: number;
  readonly uuid: string;
  readonly type:
    | 'XLINE' // extends from -Infinity to +Infinity
    | 'RAY' // extends to +/-Infinity in one direction
    | 'LINE'; // extends between the set bounds
  readonly onDrag: (
    e: React.MouseEvent,
    uuid: string | undefined,
    dragData: TrendLineDragData,
    moreProps: MoreProps,
  ) => void;
  readonly onEdge1Drag: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onEdge2Drag: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onDragComplete?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onSelect?: (e: React.MouseEvent, uuid: string, moreProps: MoreProps) => void;
  readonly r: number;
  readonly direction?: string;
  readonly strokeOpacity: number;
  readonly defaultClassName?: string;
  readonly selected?: boolean;
  readonly strokeStyle: string;
  readonly strokeWidth: number;
  readonly strokeDasharray: strokeDashTypes;
  readonly edgeStrokeWidth: number;
  readonly edgeStroke: string;
  readonly edgeInteractiveCursor: string;
  readonly lineInteractiveCursor: string;
  readonly edgeFill: string;
  readonly barsRange?: number;
  readonly priceRange?: number;
  readonly daysBetween?: { days: number; hours: number; minutes: number };
  readonly totalPercentChange?: number;
  readonly angle?: number;
  readonly showPriceRange?: boolean;
  readonly showPercentChange?: boolean;
  readonly showBarsRange?: boolean;
  readonly showDateTimeRange?: boolean;
  readonly showAngle?: boolean;
  readonly alwaysShow?: boolean;
  readonly extendLeft: boolean;
  readonly extendRight: boolean;
  readonly arrowDirection: 'left' | 'right' | 'both' | 'none';
  readonly hoverText: HoverTextConfig;
  readonly onDragStart?: (uuid: string) => void;
  readonly onHover?: (uuid: string | null, moreProps?: MoreProps) => void;
  readonly locked: boolean;
  readonly onDoubleClick?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly fillStyle?: string;
  readonly middle?: {
    strokeStyle: string;
    strokeWidth: number;
    strokeDasharray: strokeDashTypes;
    show: boolean;
  };
  readonly dy?: number; // Used for triangle shape to adjust the y position of the second point,
  readonly leftArrow?: boolean;
  readonly rightArrow?: boolean;
  readonly timeFrame?: string;
  readonly overrideData?: TransientBroadcastDragOverride;
  readonly onOverride?: (newData: TransientBroadcastDragOverride) => void;
  readonly chartId?: string;
  readonly isTemp?: boolean; // For volume profile temporary drawing
  readonly appearance?: Record<string, unknown>; // For volume profile appearance settings
  readonly sharedState: DrawingSharedState;
  readonly bgStyle?: string;
  readonly textColor?: string;
  readonly isAnyShapeEnabled?: boolean;
}

interface EachTrendLineState {
  anchor?: string;
  hover?: boolean;
  barsRange?: number;
  daysBetween?: { days: number; hours: number; minutes: number };
  priceRange?: number;
  totalPercentChange?: number;
  angle?: number;
  dragging?: boolean;
}

export class EachTrendLine extends React.Component<EachTrendLineProps, EachTrendLineState> {
  public static defaultProps = {
    onDrag: noop,
    onEdge1Drag: noop,
    onEdge2Drag: noop,
    selected: false,
    ...fixedAppearance,
    strokeWidth: 1,
    strokeDasharray: 'Solid',
    extendLeft: false,
    extendRight: false,
    arrowDirection: 'none',
    hoverText: {
      enable: false,
    },
    showBarsRange: false,
    showDateTimeRange: false,
    showPercentChange: false,
    showPriceRange: false,
    alwaysShow: false,
    locked: false,
  };

  private dragStart: { x1Value: number; y1Value: number; x2Value: number; y2Value: number } | null = null;

  private saveNodeType: <T>(type: string) => React.Ref<T>;
  prevXDomain = null;
  // @ts-expect-error
  private lineInteractive: string;

  public constructor(props: EachTrendLineProps) {
    super(props);

    this.saveNodeType = saveNodeType.bind(this);

    this.state = {
      hover: false,
      barsRange: this.props.barsRange,
      daysBetween: this.props.daysBetween,
      priceRange: this.props.priceRange,
      totalPercentChange: this.props.totalPercentChange,
      angle: this.props.angle,
    };
  }

  public render() {
    const {
      x1Value,
      y1Value,
      x2Value,
      y2Value,
      type,
      textColor,
      strokeStyle,
      strokeWidth,
      strokeDasharray,
      r,
      edgeStrokeWidth,
      edgeFill,
      edgeStroke,
      hoverText,
      selected,
      showBarsRange,
      showDateTimeRange,
      showPercentChange,
      showPriceRange,
      showAngle,
      alwaysShow,
      direction,
      arrowDirection,
      extendLeft,
      extendRight,
      edgeInteractiveCursor,
      locked,
      onDoubleClick,
      barsRange,
      priceRange,
      daysBetween,
      totalPercentChange,
      angle,
      leftArrow,
      rightArrow,
      bgStyle,
    } = this.props;

    const {
      enable: hoverTextEnabled,
      selectedText: hoverTextSelected,
      text: hoverTextUnselected,
      ...restHoverTextProps
    } = hoverText;

    const { hover, dragging } = this.state;
    // let lineInteractive;
    if (dragging) {
      this.lineInteractive = 'react-financial-charts-grabbing-cursor';
    } else if (!dragging) {
      this.lineInteractive = 'react-financial-charts-pointer-cursor';
    } else {
      this.lineInteractive = 'react-financial-charts-pointer-cursor';
    }
    const lockedR = locked ? r - 2 : r;
    const lockedEdgeStorke = locked ? '#436fe9' : edgeStroke;
    const lockedStrokeWidth = locked ? 1 : edgeStrokeWidth;
    const combinedText =
      direction === undefined
        ? getCombinedTrendLabel({
            showPriceRange: showPriceRange,
            showPercentChange: showPercentChange,
            showBarsRange: showBarsRange,
            showDateTimeRange: showDateTimeRange,
            showAngle: showAngle,
            priceRange: priceRange,
            totalPercentChange: totalPercentChange,
            barsRange: barsRange,
            daysBetween: daysBetween,
            angle: angle,
          })
        : undefined;

    const offset = getLabelOffset(y1Value, y2Value);

    const hasValidLabel = direction === undefined && typeof combinedText === 'string' && combinedText.trim().length > 0;

    const labelPosition: [number, number] | undefined = hasValidLabel
      ? [(x1Value + x2Value) / 2, (y1Value + y2Value) / 2 + offset]
      : undefined;

    return (
      <g>
        {hasValidLabel &&
          // First Text component with condition based on alwaysShow and selected
          ((!alwaysShow && selected) || alwaysShow || hover) && (
            <InteractiveText
              selected={selected}
              position={labelPosition}
              bgFillStyle={bgStyle!}
              bgStroke={bgStyle!}
              bgStrokeWidth={0}
              textFill={textColor!}
              fontFamily="Arial"
              fontStyle="normal"
              fontWeight="normal"
              fontSize={12}
              text={combinedText}
              showBackground={true}
              paddingX={10}
              paddingY={8}
            />
          )}
        <InteractiveStraightLine
          ref={this.saveNodeType('line')}
          selected={selected || hover}
          onHover={this.handleHover}
          onUnHover={this.handleUnhover}
          x1Value={x1Value}
          y1Value={y1Value}
          x2Value={x2Value}
          y2Value={y2Value}
          type={type}
          strokeStyle={strokeStyle}
          strokeWidth={strokeWidth}
          strokeDasharray={strokeDasharray}
          interactiveCursorClass={this.lineInteractive}
          onDragStart={this.handleLineDragStart}
          onDrag={this.handleLineDrag}
          onDragComplete={this.handleDragComplete}
          extendLeft={extendLeft}
          extendRight={extendRight}
          onMouseDown={this.handleShapeMouseDown}
          onDoubleClick={onDoubleClick}
        />
        {direction !== 'Horz' ? (
          <>
            {arrowDirection !== 'none' && (arrowDirection === 'left' || arrowDirection === 'both') && (
              <ClickableArrowHead
                ref={this.saveNodeType('edge1')}
                show={leftArrow || hover || locked}
                startXY={[x1Value, y1Value]}
                endXY={[x2Value, y2Value]}
                r={lockedR}
                arrowDirection={arrowDirection}
                fillStyle={edgeFill}
                strokeStyle={strokeStyle}
                strokeWidth={strokeWidth}
                strokeDasharray={strokeDasharray}
                interactiveCursorClass={edgeInteractiveCursor}
                onDragStart={this.handleEdge1DragStart}
                onDrag={this.handleEdge1Drag}
                onDragComplete={this.handleDragComplete}
              />
            )}
            <ClickableCircle
              ref={this.saveNodeType('edge1')}
              show={selected || hover || locked}
              cx={x1Value}
              cy={y1Value}
              r={lockedR}
              fillStyle={edgeFill}
              strokeStyle={lockedEdgeStorke}
              strokeWidth={lockedStrokeWidth}
              interactiveCursorClass={edgeInteractiveCursor}
              onDragStart={this.handleEdge1DragStart}
              onDrag={this.handleEdge1Drag}
              onDragComplete={this.handleDragComplete}
              onDoubleClick={onDoubleClick}
            />

            {arrowDirection !== 'none' && (arrowDirection === 'right' || arrowDirection === 'both') && (
              <ClickableArrowHead
                ref={this.saveNodeType('edge2')}
                show={rightArrow || hover || locked}
                startXY={[x1Value, y1Value]}
                endXY={[x2Value, y2Value]}
                r={lockedR}
                fillStyle={edgeFill}
                arrowDirection={arrowDirection}
                strokeStyle={strokeStyle}
                strokeWidth={strokeWidth}
                strokeDasharray={strokeDasharray}
                interactiveCursorClass={edgeInteractiveCursor}
                onDragStart={this.handleEdge2DragStart}
                onDrag={this.handleEdge2Drag}
                onDragComplete={this.handleDragComplete}
              />
            )}
            <ClickableCircle
              ref={this.saveNodeType('edge2')}
              show={selected || hover || locked}
              cx={x2Value}
              cy={y2Value}
              r={lockedR}
              fillStyle={edgeFill}
              strokeStyle={lockedEdgeStorke}
              strokeWidth={lockedStrokeWidth}
              interactiveCursorClass={edgeInteractiveCursor}
              onDragStart={this.handleEdge2DragStart}
              onDrag={this.handleEdge2Drag}
              onDragComplete={this.handleDragComplete}
              onDoubleClick={onDoubleClick}
            />
          </>
        ) : (
          <ClickableCircle
            ref={this.saveNodeType('edgeCenter')}
            show={selected || hover || locked}
            cx={x2Value}
            cy={y2Value}
            r={lockedR}
            fillStyle={edgeFill}
            strokeStyle={lockedEdgeStorke}
            strokeWidth={lockedStrokeWidth}
            onDoubleClick={onDoubleClick}
          />
        )}

        <HoverTextNearMouse
          show={hoverTextEnabled && !!hover}
          {...restHoverTextProps}
          text={selected ? hoverTextSelected : hoverTextUnselected}
        />
      </g>
    );
  }

  private readonly handleShapeMouseDown = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { onSelect, uuid, direction, sharedState } = this.props;
    if (sharedState?.status === 'started') return;
    if (direction === 'Horz') {
      this.updateCircleX2position(e, moreProps);
    }
    if (onSelect && isDefined(uuid)) {
      onSelect(e, uuid, moreProps);
    }
  };

  private readonly handleHover = (_: React.MouseEvent, moreProps: MoreProps) => {
    const { uuid, onHover, sharedState, isAnyShapeEnabled } = this.props;
    if (sharedState?.status === 'started' || isAnyShapeEnabled) return;

    const hovering = moreProps.hovering;
    if (this.state.hover !== hovering) {
      this.setState({
        hover: hovering,
      });
      if (hovering && onHover) {
        onHover(uuid ?? null, moreProps);
      }
    }
  };

  private readonly handleUnhover = (_: React.MouseEvent, moreProps: MoreProps) => {
    const hovering = moreProps.hovering;
    if (this.state.hover !== hovering) {
      this.setState({
        hover: undefined,
      });
    }
  };

  private readonly handleEdge2Drag = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { uuid, onDrag, x1Value, y1Value, direction, locked } = this.props;

    if (locked) return;
    const [x2Value, y2Value] = getNewXY(moreProps);

    // Validate values before onDrag
    if (x1Value == null || y1Value == null || x2Value == null || y2Value == null) {
      return;
    }

    if (direction !== 'Horz' && moreProps?.hovering) {
      onDrag(
        e,
        uuid,
        {
          x1Value,
          y1Value,
          x2Value,
          y2Value,
        },
        moreProps,
      );
    } else {
      if (direction === 'Horz') {
        this.updateCircleX2position(e, moreProps);
      }
    }
  };

  private readonly handleEdge1Drag = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { uuid, onDrag, x2Value, y2Value, direction, locked } = this.props;
    const [x1Value, y1Value, _xScale] = getNewXY(moreProps);
    if (locked) return;

    // Validate values before onDrag
    if (x1Value == null || y1Value == null || x2Value == null || y2Value == null) {
      return;
    }

    if (direction !== 'Horz' && moreProps?.hovering) {
      onDrag(
        e,
        uuid,
        {
          x1Value,
          y1Value,
          x2Value,
          y2Value,
        },
        moreProps,
      );
    } else {
      if (direction === 'Horz') {
        this.updateCircleX2position(e, moreProps);
      }
    }
  };

  private readonly handleDragComplete = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { onDragComplete, locked } = this.props;
    if (onDragComplete === undefined || locked) {
      return;
    }
    this.setState({
      anchor: undefined,
      dragging: false,
    });
    onDragComplete(e, moreProps);
  };

  private readonly handleEdge2DragStart = () => {
    const { onDragStart, uuid, locked } = this.props;
    if (locked) return;
    if (onDragStart && uuid !== undefined) {
      onDragStart(uuid);
    }
    this.setState({
      anchor: 'edge1',
    });
  };

  private readonly handleEdge1DragStart = () => {
    const { onDragStart, uuid, locked } = this.props;
    if (locked) return;
    if (onDragStart && uuid !== undefined) {
      onDragStart(uuid);
    }
    this.setState({
      anchor: 'edge2',
    });
  };

  private readonly handleLineDrag = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { uuid, onDrag, direction, locked } = this.props;

    if (locked) return;
    if (!this.dragStart) return;
    const { x1Value, y1Value, x2Value, y2Value } = this.dragStart;

    const {
      xScale,
      chartConfig: { yScale },
      xAccessor,
      fullData,
      hovering,
      startPos: startPosRaw,
      mouseXY,
    } = moreProps;
    const startPos = startPosRaw!;
    const fullDataArr = (fullData ?? []) as unknown[];

    if (!hovering) return;

    // Validate initial values before calculation
    if (x1Value == null || y1Value == null || x2Value == null || y2Value == null) {
      return;
    }

    const x1 = xScale(x1Value);
    const y1 = yScale(y1Value);
    const x2 = xScale(x2Value);
    const y2 = yScale(y2Value);

    // Validate scale results
    if (!isFinite(x1) || !isFinite(y1) || !isFinite(x2) || !isFinite(y2)) {
      return;
    }

    const dx = startPos[0] - mouseXY[0];
    const dy = startPos[1] - mouseXY[1];

    const newX1Value = getXValue(xScale, xAccessor, [x1 - dx, y1 - dy], fullDataArr);
    const newY1Value = yScale.invert(y1 - dy);
    const newX2Value = getXValue(xScale, xAccessor, [x2 - dx, y2 - dy], fullDataArr);
    const newY2Value = yScale.invert(y2 - dy);

    // Validate calculated values before passing to onDrag
    if (
      newX1Value == null ||
      newY1Value == null ||
      newX2Value == null ||
      newY2Value == null ||
      !isFinite(newY1Value) ||
      !isFinite(newY2Value)
    ) {
      return;
    }
    if (direction !== 'Horz' && direction !== 'Vert') {
      onDrag(
        e,
        uuid,
        {
          x1Value: newX1Value,
          y1Value: newY1Value,
          x2Value: newX2Value,
          y2Value: newY2Value,
        },
        moreProps,
      );
    } else if (direction === 'Horz') {
      onDrag(
        e,
        uuid,
        {
          x1Value,
          y1Value: newY1Value,
          x2Value,
          y2Value: newY2Value,
        },
        moreProps,
      );
    }
  };

  private readonly handleLineDragStart = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { x1Value, y1Value, x2Value, y2Value, onDragStart, uuid, direction, locked } = this.props;
    if (direction === 'Horz') {
      this.updateCircleX2position(e, moreProps);
    }
    if (locked) return;

    // Validate values before starting drag
    if (x1Value == null || y1Value == null || x2Value == null || y2Value == null) {
      return;
    }

    if (onDragStart && uuid !== undefined) {
      onDragStart(uuid);
    }
    this.setState({ dragging: true });
    this.dragStart = {
      x1Value,
      y1Value,
      x2Value,
      y2Value,
    };
  };

  private readonly updateCircleX2position = (e: React.MouseEvent, moreProps: MoreProps) => {
    const { uuid, onDrag, y1Value, x1Value, y2Value, direction } = this.props;

    const {
      xScale,
      chartConfig: { yScale: _yScale },
    } = moreProps;

    // if (locked) return;

    if (direction === 'Horz') {
      const [_min, max] = xScale.domain();
      const cxVisible = max - 10;
      onDrag(
        e,
        uuid,
        {
          x1Value,
          y1Value,
          x2Value: cxVisible,
          y2Value,
        },
        moreProps,
      );
    }
  };
}
